/**
 * Folds completed measurement draws and annotation deletions from Redux
 * back into the Report editor state.
 *
 *  - capture: when measurementStatus === 'completed', append the new
 *    measurement row (preserving uuid for existing rows so PATCH updates
 *    rather than creates) and dispatch `clearMeasurement()`.
 *  - deletion: when an annotation UID is marked deleted, null out the
 *    corresponding row's value + annotation.
 */

import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { notification } from 'antd';
import {
  selectActiveMeasurement,
  selectMeasurementStatus,
  selectPendingValue,
  selectPendingAnnotationMeta,
  selectDeletedAnnotationUID,
  selectAnnotationToMeasurement,
  clearMeasurement,
  clearDeletedAnnotation,
  mapAnnotationToMeasurement,
} from '@/state/slices/measurementSession';
import { Measurement, Report as MReport } from '@/models/report';
import type { MeasurementGroup } from '@/models/template';
import { runCascade } from '../utils/runCascade';

type NotificationApi = ReturnType<typeof notification.useNotification>[0];

interface UseMeasurementCaptureArgs {
  currentReport: MReport | null;
  setCurrentReport: (next: MReport) => void;
  /** Worksheet groups — the AP-731 engine's scope and formula source (R3). */
  measurementGroups?: MeasurementGroup[];
  patientSex?: string | null;
  notification: NotificationApi;
}

export function useMeasurementCapture({
  currentReport,
  setCurrentReport,
  measurementGroups,
  patientSex,
  notification: api,
}: UseMeasurementCaptureArgs): void {
  const dispatch = useDispatch();
  const activeMeasurement = useSelector(selectActiveMeasurement);
  const measurementStatus = useSelector(selectMeasurementStatus);
  const pendingValue = useSelector(selectPendingValue);
  const pendingAnnotationMeta = useSelector(selectPendingAnnotationMeta);
  const deletedAnnotationUID = useSelector(selectDeletedAnnotationUID);
  const annotationToMeasurement = useSelector(selectAnnotationToMeasurement);

  useEffect(() => {
    if (
      measurementStatus !== 'completed' ||
      pendingValue === null ||
      !activeMeasurement ||
      !currentReport
    ) {
      return;
    }

    const existingValues = currentReport.measurements || [];
    const existingMeasurement = existingValues.find(
      (m) => m.canonicalMeasurement === activeMeasurement.canonicalMeasurement
    );
    const filteredValues = existingValues.filter(
      (m) => m.canonicalMeasurement !== activeMeasurement.canonicalMeasurement
    );

    const newMeasurementValue: Measurement = {
      // Carry forward the existing row's library metadata
      // (canonicalMeasurementKey, type, tool, referenceRanges). Re-measuring
      // rebuilds the row, and the engine resolves operands by
      // canonicalMeasurementKey — dropping it breaks every formula using it.
      ...existingMeasurement,
      canonicalMeasurement: activeMeasurement.canonicalMeasurement,
      value: pendingValue,
      unit: activeMeasurement.unit || existingMeasurement?.unit || 'cm',
      uuid: existingMeasurement?.uuid,
      createdAt: existingMeasurement?.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      annotation: pendingAnnotationMeta
        ? {
            type: pendingAnnotationMeta.type,
            dicomUuid: pendingAnnotationMeta.dicomUuid,
            frameNumber: pendingAnnotationMeta.frameNumber,
            points: pendingAnnotationMeta.points,
          }
        : null,
    };

    setCurrentReport({
      ...currentReport,
      // R5/R8 — a draw is a reader edit of that component, so it cascades.
      measurements: runCascade({
        report: currentReport,
        updated: [...filteredValues, newMeasurementValue],
        target: activeMeasurement.canonicalMeasurement,
        action: 'edit',
        measurementGroups,
        patientSex,
      }),
    });

    if (pendingAnnotationMeta?.annotationUID) {
      dispatch(
        mapAnnotationToMeasurement({
          annotationUID: pendingAnnotationMeta.annotationUID,
          canonicalMeasurement: activeMeasurement.canonicalMeasurement,
        })
      );
    }

    api.success({
      message: 'Measurement captured',
      description: `${activeMeasurement.measurementLabel}: ${typeof pendingValue === 'number' ? pendingValue.toFixed(2) : pendingValue} ${activeMeasurement.unit}`,
    });
    dispatch(clearMeasurement());
  }, [
    measurementStatus,
    pendingValue,
    pendingAnnotationMeta,
    activeMeasurement,
    currentReport,
    dispatch,
    api,
    setCurrentReport,
    measurementGroups,
    patientSex,
  ]);

  useEffect(() => {
    if (!deletedAnnotationUID || !currentReport) return;
    const canonicalMeasurement = annotationToMeasurement[deletedAnnotationUID];
    if (canonicalMeasurement) {
      // A drawn value is manual, so deleting it clears the row and stays
      // MANUAL: no fallback to the SR or AI reading (AP-731 R8/R7). The AI
      // line is soft-deleted so it doesn't shadow a fresh draw.
      const withDeletion = currentReport.measurements.map((m) =>
        m.canonicalMeasurement === canonicalMeasurement
          ? {
              ...m,
              value: null,
              valueSource: 'MANUAL' as const,
              annotation: null,
            }
          : m
      );
      setCurrentReport({
        ...currentReport,
        aiMeasurements: (currentReport.aiMeasurements ?? []).map((ai) =>
          ai.canonicalMeasurement === canonicalMeasurement
            ? { ...ai, isDeleted: true }
            : ai
        ),
        // R7 — deleting the annotation deletes the value.
        measurements: runCascade({
          report: currentReport,
          updated: withDeletion,
          target: canonicalMeasurement,
          action: 'delete',
          measurementGroups,
          patientSex,
        }),
      });
    }
    dispatch(clearDeletedAnnotation());
  }, [
    deletedAnnotationUID,
    annotationToMeasurement,
    currentReport,
    dispatch,
    setCurrentReport,
    measurementGroups,
    patientSex,
  ]);
}
